import { useEffect, useMemo, useState } from "react";
import { ArrowRight, ChevronLeft, ChevronRight, Play } from "lucide-react";
import { useWebsiteContent } from "@/hooks/useWebsiteContent";
import { useCoachContext } from "@/contexts/CoachContext";
import { supabase } from "@/integrations/supabase/client";
import { Carousel, CarouselApi, CarouselContent, CarouselItem } from "@/components/ui/carousel";
import { Button } from "@/components/ui/button";
import heroBanner from "@/assets/hero-banner.jpg";

interface HeroVideo {
  id: string;
  youtube_video_id: string;
  title: string;
  description: string | null;
  custom_thumbnail_url: string | null;
}

const AUTO_ROTATE_MS = 7000;

const Hero = () => {
  const { content } = useWebsiteContent();
  const { coachId } = useCoachContext();
  const [videos, setVideos] = useState<HeroVideo[]>([]);
  const [isLoadingVideos, setIsLoadingVideos] = useState(true);
  const [carouselApi, setCarouselApi] = useState<CarouselApi>();
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const fetchHeroVideos = async () => {
      let query = supabase
        .from("featured_videos")
        .select("id, youtube_video_id, title, description, custom_thumbnail_url")
        .eq("is_active", true)
        .order("display_order")
        .limit(6);

      query = coachId ? query.eq("coach_id", coachId) : query.is("coach_id", null);

      const { data, error } = await query;

      if (error) {
        console.error("Error loading hero videos:", error);
      } else {
        setVideos(data || []);
      }

      setIsLoadingVideos(false);
    };

    fetchHeroVideos();
  }, [coachId]);

  useEffect(() => {
    if (!carouselApi) return;

    const updateActiveIndex = () => {
      setActiveIndex(carouselApi.selectedScrollSnap());
    };

    updateActiveIndex();
    carouselApi.on("select", updateActiveIndex);
    carouselApi.on("reInit", updateActiveIndex);

    return () => {
      carouselApi.off("select", updateActiveIndex);
      carouselApi.off("reInit", updateActiveIndex);
    };
  }, [carouselApi]);

  useEffect(() => {
    if (!carouselApi || videos.length <= 1) return;

    const interval = window.setInterval(() => {
      if (carouselApi.canScrollNext()) {
        carouselApi.scrollNext();
      } else {
        carouselApi.scrollTo(0);
      }
    }, AUTO_ROTATE_MS);

    return () => window.clearInterval(interval);
  }, [carouselApi, videos.length]);

  const activeVideo = useMemo(() => videos[activeIndex] ?? null, [videos, activeIndex]);

  return (
    <section
      className="relative overflow-hidden text-background"
      style={{
        backgroundImage: `url(${heroBanner})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <div className="absolute inset-0 bg-slate-950/75" />
      <div className="absolute inset-0 bg-gradient-to-br from-slate-950/85 via-slate-950/70 to-slate-900/80" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6 pt-24 pb-14 md:pt-28 md:pb-20">
        <div className="grid gap-10 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)] lg:items-center">
          <div className="text-center lg:text-left">
            <div className="inline-flex items-center gap-2 rounded-full border border-background/15 bg-background/10 px-4 py-2 text-xs font-medium uppercase tracking-[0.22em] text-background/75 backdrop-blur-sm">
              <Play className="h-3.5 w-3.5" />
              Home video carousel
            </div>

            <h1 className="mt-6 text-5xl sm:text-6xl md:text-[80px] lg:text-[96px] font-semibold text-background leading-[1.05] tracking-[-0.03em]">
              {content.hero_headline?.split("&").map((part, index) => (
                <span key={index}>
                  {index === 0 ? (
                    <>{part.trim()}</>
                  ) : (
                    <>
                      <br />
                      <span className="text-background/75">&amp; {part.trim()}</span>
                    </>
                  )}
                </span>
              )) || content.hero_headline}
            </h1>

            <p className="mt-4 text-xl md:text-[28px] text-background/85 font-normal leading-snug max-w-2xl mx-auto lg:mx-0">
              {content.hero_subheadline}
            </p>

            <div className="flex flex-wrap items-center justify-center lg:justify-start gap-6 mt-6">
              <a
                href="#contact"
                className="inline-flex items-center gap-1 text-background text-xl font-normal hover:underline"
              >
                Get Started <ArrowRight className="w-5 h-5" />
              </a>
              <a
                href="#services"
                className="inline-flex items-center gap-1 text-background/85 text-xl font-normal hover:underline hover:text-background"
              >
                Explore Platform Features <ArrowRight className="w-5 h-5" />
              </a>
            </div>

            <div className="mt-10 flex items-center justify-center lg:justify-start gap-0 max-w-lg lg:max-w-none mx-auto lg:mx-0">
              {[
                { value: content.hero_stat_1_value, label: content.hero_stat_1_label },
                { value: content.hero_stat_2_value, label: content.hero_stat_2_label },
                { value: content.hero_stat_3_value, label: content.hero_stat_3_label },
              ].map((stat, i) => (
                <div key={i} className={`flex-1 text-center lg:text-left ${i > 0 ? "border-l border-background/20" : ""} px-5 lg:px-6`}>
                  <div className="text-3xl sm:text-4xl font-semibold text-background tracking-tight">
                    {stat.value}
                  </div>
                  <div className="text-[11px] text-background/75 mt-1 font-medium uppercase tracking-[0.1em]">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="relative">
            {videos.length > 0 ? (
              <div className="rounded-[28px] border border-white/10 bg-white/5 p-3 shadow-2xl shadow-black/30 backdrop-blur-sm">
                <Carousel opts={{ align: "start", loop: true }} setApi={setCarouselApi} className="w-full">
                  <CarouselContent className="ml-0">
                    {videos.map((video) => (
                      <CarouselItem key={video.id} className="pl-0">
                        <div className="overflow-hidden rounded-3xl bg-slate-950">
                          <div className="relative aspect-video">
                            <iframe
                              src={`https://www.youtube.com/embed/${video.youtube_video_id}?rel=0&modestbranding=1&playsinline=1`}
                              title={video.title}
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                              className="absolute inset-0 h-full w-full"
                            />
                          </div>
                        </div>
                      </CarouselItem>
                    ))}
                  </CarouselContent>
                </Carousel>

                <div className="mt-4 flex items-start justify-between gap-4">
                  <div>
                    <p className="text-xs uppercase tracking-[0.18em] text-background/70">Featured video</p>
                    <h2 className="mt-1 text-lg md:text-xl font-semibold text-background">
                      {activeVideo?.title}
                    </h2>
                    {activeVideo?.description && (
                      <p className="mt-1 text-sm text-background/80 line-clamp-2">
                        {activeVideo.description}
                      </p>
                    )}
                  </div>

                  {videos.length > 1 && (
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="h-10 w-10 rounded-full border-white/15 bg-white/5 text-background hover:bg-white/10"
                        onClick={() => carouselApi?.scrollPrev()}
                      >
                        <ChevronLeft className="h-4 w-4" />
                        <span className="sr-only">Previous video</span>
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="h-10 w-10 rounded-full border-white/15 bg-white/5 text-background hover:bg-white/10"
                        onClick={() => carouselApi?.scrollNext()}
                      >
                        <ChevronRight className="h-4 w-4" />
                        <span className="sr-only">Next video</span>
                      </Button>
                    </div>
                  )}
                </div>

                {videos.length > 1 && (
                  <div className="mt-4 flex flex-wrap items-center gap-2">
                    {videos.map((video, index) => (
                      <button
                        key={video.id}
                        type="button"
                        onClick={() => carouselApi?.scrollTo(index)}
                        className={`group flex items-center gap-3 rounded-full border px-2 py-2 pr-3 text-left transition ${
                          index === activeIndex
                            ? "border-white/20 bg-white/12"
                            : "border-white/10 bg-white/[0.04] hover:bg-white/10"
                        }`}
                      >
                        <img
                          src={video.custom_thumbnail_url || `https://img.youtube.com/vi/${video.youtube_video_id}/mqdefault.jpg`}
                          alt={video.title}
                          width={64}
                          height={40}
                          loading="lazy"
                          decoding="async"
                          className="h-10 w-16 rounded-full object-cover"
                        />

                        <span className="max-w-[10rem] text-xs font-medium text-background/80 line-clamp-2">
                          {video.title}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="rounded-[28px] border border-white/10 bg-white/5 p-6 md:p-8 shadow-2xl shadow-black/30 backdrop-blur-sm">
                <div className="overflow-hidden rounded-3xl border border-white/10 bg-slate-950/60">
                  <img
                    src={heroBanner}
                    alt="GoCoachify hero banner"
                    width={1600}
                    height={900}
                    fetchPriority="high"
                    decoding="async"
                    className="aspect-video w-full object-cover opacity-80"
                  />

                </div>
                <div className="mt-5">
                  <p className="text-xs uppercase tracking-[0.18em] text-background/70">Banner ready for video</p>
                  <h2 className="mt-1 text-lg md:text-xl font-semibold text-background">
                    Add YouTube videos in admin to power this homepage carousel.
                  </h2>
                  <p className="mt-1 text-sm text-background/80">
                    When featured videos are active, the home banner automatically rotates through them.
                  </p>
                </div>
              </div>
            )}

            {isLoadingVideos && (
              <div className="absolute inset-0 rounded-[28px] border border-white/10 bg-slate-950/35 backdrop-blur-[2px]" />
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
